"use client"

import { useState } from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ChevronDown, Search, SlidersHorizontal } from "lucide-react"
import type { Animal } from "@/lib/types"

export function AnimalTable({ animal }: { animal: Animal }) {
  const [searchTerm, setSearchTerm] = useState("")

  // Convert the animal data object to an array of key-value pairs
  const animalDataArray = Object.entries(animal)
    .filter(
      ([key]) =>
        // Filter out complex objects and arrays
        key !== "_id" &&
        key !== "imageUrls" &&
        key !== "videoUrls" &&
        key !== "createdAt" &&
        key !== "updatedAt" &&
        key !== "__v",
    )
    .filter(
      ([key, value]) =>
        // Apply search filter
        searchTerm === "" ||
        key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => a[0].localeCompare(b[0]))

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search metrics..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-1">
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Filter</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            <DropdownMenuLabel>Filter by Category</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSearchTerm("weight")}>Weight & Size</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSearchTerm("breed")}>Breeding & Genetics</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSearchTerm("farm")}>Farm Information</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSearchTerm("")}>Clear Filters</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Metric</TableHead>
              <TableHead>Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {animalDataArray.map(([key, value]) => (
              <TableRow key={key} className="group">
                <TableCell className="font-medium">
                  {key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/_/g, " ")
                    .replace(/^./, (str) => str.toUpperCase())}
                </TableCell>
                <TableCell>{typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
